import React from 'react';
import ReactDOM from 'react-dom';
import FullFAQSection from './site/embeddables/FullFAQSection';
import FAQSection from './site/embeddables/FAQSection';
import {faqRefusalNotice, getFAQAnalyticsBootstrapUrl, loadFAQSectionData} from './site/embeddables/utils';
import {renderTemplatedSection, templateOf} from './site/embeddables/templated';
import {appendCustomCss} from './site/embeddables/customCss';
import {trackWithBootstrap} from './site/analytics/host-beacon';
import {each} from 'lodash';

/*
 * Draws every FAQ section placeholder on the page. The section's own data
 * decides how: one that picked a template renders with it
 * (site/embeddables/templated.js); every other one with the classic React
 * renderer, whose own read of the data is answered from the cache this first
 * read fills. Either way the section brings its custom CSS
 * (site/embeddables/customCss.js).
 */
(function() {
    const options = window.hcOptions || {};
    // The language the classic renderer reads in, so both reads share one URL.
    const lang = options.lang || 'default';

    /**
     * The custom CSS goes in after the render, not before: a first
     * ReactDOM.render() empties its container. React only touches the nodes it
     * made, so the style element stays put while the section updates.
     */
    function renderClassic(section, sectionId, data) {
        const sectionType = sectionId !== 'bYjEs' ? section.getAttribute('data-type') : 'custom';
        if (sectionType === 'full') {
            ReactDOM.render(<FullFAQSection id={sectionId} />, section);
        } else {
            ReactDOM.render(<FAQSection id={sectionId} />, section);
        }
        appendCustomCss(section, data.section);
    }

    /**
     * A page can hide a placeholder's fallback content while it waits for
     * this script (the v2 share page does), so it does not show in one style
     * and then another. Whatever the outcome, the wait is over.
     */
    function settle(section) {
        if (section.hasAttribute('data-hcfq-pending')) {
            section.removeAttribute('data-hcfq-pending');
            section.style.visibility = '';
        }
    }

    function showRefusal(section, reason) {
        const notice = faqRefusalNotice(reason);
        section.innerHTML = '';
        if (notice) {
            const paragraph = document.createElement('p');
            paragraph.className = 'hc-faqs-unavailable';
            paragraph.setAttribute('role', 'status');
            paragraph.textContent = notice;
            section.appendChild(paragraph);
        }
    }

    // A page that asked for static sections (`staticFaqS`) has had init.js
    // fill in every section the server could draw; it marks the ones only
    // this script can, and loads it for those alone.
    const onlyHandedOver = !!options.staticFaqS;
    // Set on a placeholder this script has taken, so a later scan draws only
    // the ones that are new: drawing one twice would count its page view
    // twice and add its custom CSS twice.
    const RENDERED = 'data-hcfq-rendered';

    function scan() {
        each(Array.from(document.getElementsByClassName('hc-faq-section')), renderSection);
    }

    function renderSection(section) {
        if (section.hasAttribute(RENDERED)) {
            return;
        }
        if (onlyHandedOver && !section.hasAttribute('data-hcfq-client')) {
            return;
        }
        section.setAttribute(RENDERED, '');

        const sectionId = section.getAttribute('data-id');
        loadFAQSectionData(sectionId, data => {
            settle(section);
            if (!templateOf(data.section)) {
                renderClassic(section, sectionId, data);
                return;
            }

            const analyticsBootstrapUrl = getFAQAnalyticsBootstrapUrl(sectionId);
            renderTemplatedSection(section, data, {
                lang,
                analyticsBootstrapUrl,
                helpCenterPage: section.hasAttribute('data-hcfq-help-center-page'),
            });
            trackWithBootstrap(analyticsBootstrapUrl, 'trackPageView', {
                faqSectionId: sectionId,
            }).catch(() => null);
        }, false, lang, reason => {
            settle(section);
            showRefusal(section, reason);
        });
    }

    // init.js calls this from hcWidget.refresh(), for sections a single-page
    // app renders after this script first ran.
    window.__hcioFaqSections = { scan };
    scan();
})();
